import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Tag,
  Space,
  Modal,
  Form,
  Typography,
  Statistic,
  Row,
  Col,
  Tooltip,
  Popconfirm,
  DatePicker,
  Badge,
  App,
  message as antdMessage,
} from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import {
  PlusOutlined,
  CopyOutlined,
  SearchOutlined,
  DeleteOutlined,
  StopOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import api from '@/api/client';
import { useAdminStore } from '@/stores/useAdminStore';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface BetaInviteCode {
  id: number;
  code: string;
  description: string;
  status: 'active' | 'used' | 'disabled';
  created_at: string;
  used_at: string | null;
  expires_at: string | null;
  created_by_name: string;
  used_by_id: number | null;
  used_by_username: string | null;
  used_by_display_name: string | null;
}

interface BetaInviteStats {
  total: number;
  active_count: number;
  used_count: number;
  disabled_count: number;
}

export default function BetaInvitesPage() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BetaInviteCode[]>([]);
  const [stats, setStats] = useState<BetaInviteStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'used' | 'disabled'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const { message } = App.useApp();

  const fetchStats = async () => {
    try {
      const res = await api.get('/beta-invites/stats');
      setStats(res.data);
    } catch (err: any) {
      console.error('获取统计失败', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/beta-invites', {
        params: {
          page,
          pageSize,
          status: statusFilter,
          keyword: keyword || undefined,
        },
      });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch (err: any) {
      message.error(err.response?.data?.error || '获取列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchData();
  }, [page, pageSize, statusFilter]);

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  const handleCreate = async (values: any) => {
    try {
      const res = await api.post('/beta-invites', {
        count: values.count,
        description: values.description,
        expiresDays: values.expiresDays,
      });
      message.success(res.data.message);
      setGeneratedCodes(res.data.codes);
      setIsModalOpen(false);
      setIsResultModalOpen(true);
      form.resetFields();
      fetchStats();
      fetchData();
    } catch (err: any) {
      message.error(err.response?.data?.error || '创建失败');
    }
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      message.success('已复制到剪贴板');
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      message.success('已复制到剪贴板');
    }
  };

  const handleCopyAll = async () => {
    const text = generatedCodes.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      message.success('已复制所有邀请码');
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      message.success('已复制所有邀请码');
    }
  };

  const handleDisable = async (id: number) => {
    try {
      await api.put(`/beta-invites/${id}/disable`);
      message.success('已禁用');
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.error || '禁用失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/beta-invites/${id}`);
      message.success('已删除');
      fetchData();
      fetchStats();
    } catch (err: any) {
      message.error(err.response?.data?.error || '删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的邀请码');
      return;
    }
    Modal.confirm({
      title: '批量删除确认',
      content: `确定删除选中的 ${selectedRowKeys.length} 个邀请码吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.post('/beta-invites/batch-delete', { ids: selectedRowKeys });
          message.success(`成功删除 ${selectedRowKeys.length} 个邀请码`);
          setSelectedRowKeys([]);
          fetchData();
          fetchStats();
        } catch (err: any) {
          message.error(err.response?.data?.error || '批量删除失败');
        }
      },
    });
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'active':
        return <Tag color="success">未使用</Tag>;
      case 'used':
        return <Tag color="default">已使用</Tag>;
      case 'disabled':
        return <Tag color="error">已禁用</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns = [
    {
      title: '邀请码',
      dataIndex: 'code',
      width: 150,
      render: (code: string, record: BetaInviteCode) => (
        <Space>
          <Text code copyable={{ text: code }}>
            {code}
          </Text>
          <Tooltip title="复制">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(code)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: getStatusTag,
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
      render: (desc: string) => desc || '-',
    },
    {
      title: '创建信息',
      width: 180,
      render: (_: any, record: BetaInviteCode) => (
        <div>
          <div>{record.created_by_name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(record.created_at).format('YYYY-MM-DD HH:mm')}
          </Text>
        </div>
      ),
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      width: 150,
      render: (expires: string | null) => {
        if (!expires) return <Text type="secondary">永不过期</Text>;
        const isExpired = dayjs(expires).isBefore(dayjs());
        return (
          <Text type={isExpired ? 'danger' : 'secondary'}>
            {dayjs(expires).format('YYYY-MM-DD')}
            {isExpired && ' (已过期)'}
          </Text>
        );
      },
    },
    {
      title: '使用者',
      width: 200,
      render: (_: any, record: BetaInviteCode) => {
        if (record.status !== 'used') return '-';
        return (
          <div>
            <div>{record.used_by_display_name || record.used_by_username}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ID: {record.used_by_id}
            </Text>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(record.used_at).format('YYYY-MM-DD HH:mm')}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: '操作',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: BetaInviteCode) => (
        <Space>
          {record.status === 'active' && (
            <Popconfirm
              title="确定禁用此邀请码？"
              onConfirm={() => handleDisable(record.id)}
            >
              <Button type="link" danger size="small" icon={<StopOutlined />}>
                禁用
              </Button>
            </Popconfirm>
          )}
          {record.status !== 'used' && (
            <Popconfirm
              title="确定删除此邀请码？"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>内测邀请码管理</Title>

      {/* 统计卡片 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总邀请码数"
                value={stats.total}
                prefix={<GiftOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="未使用"
                value={stats.active_count}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="已使用"
                value={stats.used_count}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="已禁用"
                value={stats.disabled_count}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 操作栏 */}
      <Card style={{ marginBottom: 24 }}>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalOpen(true)}
          >
            创建邀请码
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={handleBatchDelete}
          >
            批量删除{selectedRowKeys.length > 0 && ` (${selectedRowKeys.length})`}
          </Button>
          <Input
            placeholder="搜索邀请码/描述/使用者"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 250 }}
            prefix={<SearchOutlined />}
            allowClear
          />
          <Space>
            <Text>状态:</Text>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d9d9d9' }}
            >
              <option value="all">全部</option>
              <option value="active">未使用</option>
              <option value="used">已使用</option>
              <option value="disabled">已禁用</option>
            </select>
          </Space>
          <Button onClick={handleSearch}>查询</Button>
        </Space>
      </Card>

      {/* 列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps || 20);
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 创建弹窗 */}
      <Modal
        title="创建内测邀请码"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="创建"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{ count: 1 }}
        >
          <Form.Item
            label="创建数量"
            name="count"
            rules={[{ required: true, message: '请输入数量' }]}
          >
            <Input type="number" min={1} max={100} placeholder="1-100" />
          </Form.Item>
          <Form.Item
            label="描述/备注"
            name="description"
          >
            <Input.TextArea
              placeholder="可选，用于标识这批邀请码的用途"
              maxLength={255}
              showCount
              rows={3}
            />
          </Form.Item>
          <Form.Item
            label="有效期（天）"
            name="expiresDays"
            extra="留空表示永不过期"
          >
            <Input type="number" min={1} max={365} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 生成结果弹窗 */}
      <Modal
        title="邀请码创建成功"
        open={isResultModalOpen}
        onCancel={() => setIsResultModalOpen(false)}
        footer={[
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={handleCopyAll}>
            复制全部
          </Button>,
          <Button key="close" onClick={() => setIsResultModalOpen(false)}>
            关闭
          </Button>,
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>成功创建 {generatedCodes.length} 个内测邀请码：</Text>
        </div>
        <div
          style={{
            background: '#f5f5f5',
            padding: 16,
            borderRadius: 8,
            maxHeight: 300,
            overflow: 'auto',
          }}
        >
          {generatedCodes.map((code, index) => (
            <div key={index} style={{ marginBottom: 8, fontFamily: 'monospace' }}>
              {code}
            </div>
          ))}
        </div>
        <Text type="secondary" style={{ marginTop: 16, display: 'block' }}>
          请妥善保存这些邀请码，它们不会再次显示。
        </Text>
      </Modal>
    </div>
  );
}
